import React, { useEffect, useState } from "react";
import { Database, RefreshCw, Activity, HardDrive, Users } from "lucide-react";
import { GetPostgresStats } from "./apis/get";

const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
    <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="text-2xl font-black text-white mt-1">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

export default function PostgresStats() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState<any>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const res: any = await GetPostgresStats();
      if (res?.status === true && res?.data) {
        setPayload(res.data);
      } else {
        setError(res?.message || "Failed to load PostgreSQL stats");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load PostgreSQL stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const data = payload || {};
  const connections = data.connections || {};
  const db = data.database || {};
  const health = data.health || {};
  const dbSize = data.database_size || {};
  const meta = data.meta || {};
  const topTables: any[] = Array.isArray(data.top_tables) ? data.top_tables : [];
  const topIndexes: any[] = Array.isArray(data.top_indexes) ? data.top_indexes : [];

  return (
    <div className="min-h-screen p-4 md:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">PostgreSQL Stats</h1>
          <p className="text-slate-400 text-sm mt-1">{meta.db_name || "Database"} • {meta.snapshot_at || "-"}</p>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-300 text-sm">{error}</div>
      )}

      {loading && !payload ? (
        <div className="text-slate-400 text-sm">Loading PostgreSQL stats...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Connections" value={connections.total_connections ?? 0} sub={`Active: ${connections.active_connections ?? 0}`} />
            <StatCard label="Cache Hit Ratio" value={`${db.cache_hit_ratio_percent ?? 0}%`} sub={`Rollback: ${db.rollback_ratio_percent ?? 0}%`} />
            <StatCard label="Database Size" value={dbSize.database_size_pretty || "-"} sub={`${dbSize.database_size_bytes || 0} bytes`} />
            <StatCard label="Connection Utilization" value={`${db.connection_utilization_percent ?? 0}%`} sub={`Deadlocks: ${db.deadlocks ?? 0}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><Users size={14} /> Connections</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-slate-400">Active</span><span className="text-white">{connections.active_connections ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Idle</span><span className="text-white">{connections.idle_connections ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Waiting</span><span className="text-white">{connections.waiting_connections ?? 0}</span></div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><Activity size={14} /> Health</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-slate-400">Postgres Pressure</span><span className="text-emerald-400">{health.postgres_connection_pressure || "-"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Cache Health</span><span className="text-emerald-400">{health.postgres_cache_health || "-"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">PgBouncer</span><span className="text-emerald-400">{health.pgbouncer_status || "-"}</span></div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><HardDrive size={14} /> Meta</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-slate-400">DB</span><span className="text-white">{meta.db_name || "-"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Replica</span><span className="text-white">{String(meta.is_replica ?? "-")}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Version</span><span className="text-white text-right max-w-[70%] truncate">{meta.pg_version || "-"}</span></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><Database size={14} /> Top Tables</p>
              <div className="overflow-auto max-h-[320px]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-800">
                      <th className="text-left py-2">Table</th>
                      <th className="text-right py-2">Rows</th>
                      <th className="text-right py-2">Idx Scan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topTables.map((t, i) => (
                      <tr key={`${t.table_name}-${i}`} className="border-b border-slate-800/50">
                        <td className="py-2 text-slate-200">{t.table_name}</td>
                        <td className="py-2 text-right text-slate-300">{t.live_rows_estimate ?? 0}</td>
                        <td className="py-2 text-right text-slate-300">{t.idx_scan ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-2"><Database size={14} /> Top Indexes</p>
              <div className="overflow-auto max-h-[320px]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-800">
                      <th className="text-left py-2">Index</th>
                      <th className="text-right py-2">Scans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topIndexes.map((idx, i) => (
                      <tr key={`${idx.index_name}-${i}`} className="border-b border-slate-800/50">
                        <td className="py-2 text-slate-200">
                          <div className="font-semibold">{idx.index_name}</div>
                          <div className="text-slate-500 text-[10px]">{idx.table_name}</div>
                        </td>
                        <td className="py-2 text-right text-slate-300">{idx.idx_scan ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

